import { useState, useMemo } from 'react';
import { B, radius, weight } from '../constants/brand';
import { api } from '../api/client';
import { useApi } from '../hooks/useApi';
import { Btn, Spinner, ErrorMsg, PageHeader, Icon, Chip, useToast } from '../components/ui';

// Notes by Tag — the cross-deal payoff of note tags. Pick one or more tags
// (AND) and see every note carrying them across all deals; click a note to open
// its deal/task and scroll to it. Saved views persist a named tag filter.
//
// onDealClick / onTaskClick / onNoteOpen are supplied by App.jsx. onNoteOpen is
// the same note router global search uses, so a row deep-links + highlights.
export const NotesByTag = ({ onNoteOpen }) => {
  const { toast } = useToast();
  const { data: tagData, loading: tagsLoading, error: tagsError, reload: reloadTags } = useApi(() => api.get('note-tags'), []);
  const { data: views, reload: reloadViews } = useApi(() => api.get('saved-note-views'), []);

  const [selected, setSelected] = useState([]);   // normalized tag keys
  const selectedKey = selected.slice().sort().join(',');

  // Notes for the current selection (AND). Re-fetches when the selection changes.
  const { data: notes, loading: notesLoading, error: notesError, reload: reloadNotes } = useApi(
    () => (selected.length ? api.get(`notes/by-tag?tags=${encodeURIComponent(selected.join(','))}`) : Promise.resolve([])),
    [selectedKey]
  );

  const allTags = tagData?.tags || [];
  const savedViews = views || [];

  const toggle = (tag) =>
    setSelected(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);

  const applyView = (v) => setSelected(Array.isArray(v.tags) ? v.tags : []);

  const saveView = async () => {
    if (!selected.length) return;
    const name = window.prompt('Name this view (e.g. "Legal issues"):', '');
    if (!name || !name.trim()) return;
    try {
      await api.put('saved-note-views', { name: name.trim(), tags: selected });
      reloadViews();
      toast.success('View saved');
    } catch (e) { toast.error(e.message || 'Could not save view'); }
  };

  const deleteView = async (v) => {
    if (!window.confirm(`Delete saved view "${v.name}"?`)) return;
    try { await api.del(`saved-note-views/${v.savedViewId}`); reloadViews(); }
    catch (e) { toast.error(e.message || 'Could not delete view'); }
  };

  // Label lookup so selected keys render with their display casing.
  const labelFor = useMemo(() => {
    const m = {};
    for (const t of allTags) m[t.tag] = t.label;
    return (k) => m[k] || k;
  }, [allTags]);

  return (
    <div>
      <PageHeader
        icon="note"
        title="Notes by Tag"
        subtitle={selected.length
          ? `${(notes || []).length} note${(notes || []).length !== 1 ? 's' : ''} · ${selected.map(labelFor).map(l => `#${l}`).join(' + ')}`
          : 'Pick one or more tags to see matching notes across every deal'}
        actions={
          <Btn size="sm" disabled={!selected.length} onClick={saveView}>
            <Icon name="star" size={14} /> Save view
          </Btn>
        }
      />

      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
        {/* Left: tags + saved views */}
        <div style={{ width: 280, flexShrink: 0 }}>
          <div style={{ fontSize: 11, fontWeight: weight.bold, color: B.textMd, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Tags</div>
          {tagsError ? <ErrorMsg msg={tagsError} onRetry={reloadTags} /> : tagsLoading ? <Spinner /> : allTags.length === 0 ? (
            <div style={{ fontSize: 13, color: B.textLt }}>No tags yet. Add tags to notes to see them here.</div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {allTags.map(t => {
                const on = selected.includes(t.tag);
                return (
                  <Chip key={t.tag} color={on ? B.navy : B.border}
                    onClick={() => toggle(t.tag)}
                    style={{ cursor: 'pointer', fontSize: 12, color: on ? B.white : B.text, opacity: on ? 1 : 0.9 }}>
                    #{t.label} <span style={{ opacity: 0.7 }}>{t.count}</span>
                  </Chip>
                );
              })}
            </div>
          )}

          {savedViews.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: weight.bold, color: B.textMd, textTransform: 'uppercase', letterSpacing: 0.5, margin: '20px 0 8px' }}>Saved views</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {savedViews.map(v => (
                  <div key={v.savedViewId} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <button onClick={() => applyView(v)}
                      style={{ flex: 1, textAlign: 'left', padding: '6px 10px', border: `1px solid ${B.border}`, borderRadius: radius.md, background: B.surface, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, color: B.text }}>
                      {v.name}
                      <span style={{ display: 'block', fontSize: 11, color: B.textLt, marginTop: 1 }}>
                        {(v.tags || []).map(t => `#${labelFor(t)}`).join(' + ') || 'no tags'}
                      </span>
                    </button>
                    <button onClick={() => deleteView(v)} title="Delete view"
                      style={{ border: 'none', background: 'none', color: B.textLt, cursor: 'pointer', display: 'inline-flex', padding: 4 }}>
                      <Icon name="trash" size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Right: matching notes */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {!selected.length ? (
            <div style={{ textAlign: 'center', color: B.textLt, padding: '48px 0', fontSize: 14 }}>
              Select a tag on the left to browse its notes across every deal.
            </div>
          ) : notesError ? <ErrorMsg msg={notesError} onRetry={reloadNotes} /> : notesLoading ? <Spinner /> : (notes || []).length === 0 ? (
            <div style={{ textAlign: 'center', color: B.textLt, padding: '48px 0', fontSize: 14 }}>
              No notes carry {selected.length > 1 ? 'all of' : ''} {selected.map(labelFor).map(l => `#${l}`).join(' + ')}.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(notes || []).map(n => (
                <button key={n.noteId} onClick={() => onNoteOpen?.(n)}
                  style={{ textAlign: 'left', padding: '12px 14px', border: `1px solid ${B.border}`, borderRadius: radius.lg, background: B.surface, cursor: 'pointer', fontFamily: 'inherit' }}>
                  <div style={{ fontSize: 13, color: B.text, fontWeight: weight.medium, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {n.title || 'Note'}
                  </div>
                  {n.subtitle && (
                    <div style={{ fontSize: 12, color: B.textMd, marginTop: 2 }}>{n.subtitle}</div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
